<template>
  <div ref="root" class="mq-text">
    <span ref="clip" class="mq-text-clip">
      <span ref="text" class="mq-text-line">
        <slot />
      </span>
    </span>
  </div>
</template>

<script setup>
  import { onMounted, onBeforeUnmount, ref, watch } from 'vue';

  const props = defineProps({
    scrollSpeed: {
      type: Number,
      default: 60,
    },
  });

  const root = ref(null);
  const clip = ref(null);
  const text = ref(null);
  const resizeObserver = ref(null);
  const mutationObserver = ref(null);

  function clearMarqueeOverflow(rootEl) {
    rootEl.classList.remove('mq-text-o');
    rootEl.style.removeProperty('--mq-text-x');
    rootEl.style.removeProperty('--mq-text-t');
  }

  function recalculateMarquee(rootEl, clipSpan, textSpan, scrollSpeed) {
    textSpan.style.width = 'auto';
    if (textSpan.clientWidth <= clipSpan.clientWidth) {
      textSpan.style.width = '';
      clearMarqueeOverflow(rootEl);
      return;
    }
    rootEl.style.setProperty('--mq-text-x', `translateX(calc(-100% + ${clipSpan.clientWidth}px - 5px))`);
    rootEl.style.setProperty(
      '--mq-text-t',
      `transform ${(textSpan.clientWidth - clipSpan.clientWidth) / scrollSpeed}s linear`
    );
    textSpan.style.width = '';
    rootEl.classList.add('mq-text-o');
  }

  function invalidateMarquee() {
    if (!root.value || !clip.value || !text.value) return;
    recalculateMarquee(root.value, clip.value, text.value, props.scrollSpeed);
  }

  function stopMarqueeObservers() {
    resizeObserver.value?.disconnect();
    mutationObserver.value?.disconnect();
    resizeObserver.value = null;
    mutationObserver.value = null;
    if (root.value) clearMarqueeOverflow(root.value);
  }

  function startMarqueeObservers() {
    stopMarqueeObservers();
    if (!root.value || !clip.value || !text.value) return;
    const resizeObserver = new ResizeObserver(invalidateMarquee);
    const mutationObserver = new MutationObserver(invalidateMarquee);
    resizeObserver.observe(root.value);
    mutationObserver.observe(text.value, { childList: true, subtree: true, characterData: true });
    resizeObserver.value = resizeObserver;
    mutationObserver.value = mutationObserver;
    invalidateMarquee();
  }

  watch(() => props.scrollSpeed, invalidateMarquee);

  onMounted(startMarqueeObservers);

  onBeforeUnmount(stopMarqueeObservers);
</script>

<style scoped lang="scss">
  .mq-text {
    display: grid;
    min-width: 0;
    padding-right: 5px;
  }

  .mq-text-clip {
    display: inline-block;
    width: 100%;
    overflow: hidden;
    vertical-align: middle;
    font: inherit;
  }

  .mq-text-o:hover .mq-text-clip,
  .mq-text-o:focus-within .mq-text-clip {
    width: auto;
  }

  .mq-text-line {
    position: relative;
    left: 0;
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    font: inherit;
  }

  .mq-text-o:hover .mq-text-line,
  .mq-text-o:focus-within .mq-text-line {
    width: auto;
    transform: var(--mq-text-x);
    transition: var(--mq-text-t);
  }

  @media (prefers-reduced-motion: reduce) {
    .mq-text-o:hover .mq-text-clip,
    .mq-text-o:focus-within .mq-text-clip {
      width: 100%;
    }

    .mq-text-o:hover .mq-text-line,
    .mq-text-o:focus-within .mq-text-line {
      width: 100%;
      transform: none;
      transition: none;
    }
  }
</style>
